<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import format from 'date-fns/format';

import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';

const props = defineProps({
  selectedInbox: {
    type: Object,
    default: null,
  },
});

const listeningConfig = ref(null);
const isListeningLoading = ref(false);
const listeningForm = ref({
  candidateName: '',
  candidateUsername: '',
  hashtags: '',
  watchTerms: '',
  adversaryNames: '',
});
const isListeningSaving = ref(false);
const isListeningRunning = ref(false);
const isTogglingAi = ref(false);

const aiEnabled = computed(() => {
  return listeningConfig.value ? listeningConfig.value.ai_enabled !== false : true;
});

async function toggleAi() {
  if (!props.selectedInbox) return;
  isTogglingAi.value = true;
  try {
    const response = await InstagramMediaReportsAPI.saveListeningConfig({
      inboxId: props.selectedInbox.id,
      aiEnabled: !aiEnabled.value,
    });
    listeningConfig.value = response.data;
  } catch {
    // silently fail
  } finally {
    isTogglingAi.value = false;
  }
}

function formatDate(dateStr) {
  try {
    return format(new Date(dateStr), 'dd/MM');
  } catch {
    return dateStr;
  }
}

async function fetchListeningConfig() {
  if (!props.selectedInbox) return;
  isListeningLoading.value = true;
  try {
    const response = await InstagramMediaReportsAPI.getListeningConfig({
      inboxId: props.selectedInbox.id,
    });
    listeningConfig.value = response.data;
    if (response.data) {
      listeningForm.value = {
        candidateName: response.data.candidate_name || '',
        candidateUsername: response.data.candidate_username || '',
        hashtags: (response.data.hashtags || []).join(', '),
        watchTerms: (response.data.watch_terms || []).join(', '),
        adversaryNames: (response.data.adversary_names || []).join(', '),
      };
    }
  } catch {
    // silently fail
  } finally {
    isListeningLoading.value = false;
  }
}

async function saveListeningConfig() {
  if (!props.selectedInbox) return;
  isListeningSaving.value = true;
  try {
    const payload = {
      inboxId: props.selectedInbox.id,
      candidateName: listeningForm.value.candidateName.trim(),
      candidateUsername: listeningForm.value.candidateUsername.trim(),
      hashtags: listeningForm.value.hashtags
        .split(',')
        .map(h => h.trim())
        .filter(Boolean),
      watchTerms: listeningForm.value.watchTerms
        .split(',')
        .map(w => w.trim())
        .filter(Boolean),
      adversaryNames: listeningForm.value.adversaryNames
        .split(',')
        .map(a => a.trim())
        .filter(Boolean),
    };
    const response =
      await InstagramMediaReportsAPI.saveListeningConfig(payload);
    listeningConfig.value = response.data;
  } catch {
    // silently fail
  } finally {
    isListeningSaving.value = false;
  }
}

async function runListening() {
  if (!props.selectedInbox) return;
  isListeningRunning.value = true;
  try {
    await InstagramMediaReportsAPI.runListening({
      inboxId: props.selectedInbox.id,
    });
    // Escuta roda em segundo plano - recarrega a cada 10s por 2 minutos
    let checks = 0;
    const maxChecks = 12;
    const pollInterval = setInterval(async () => {
      checks += 1;
      await fetchListeningConfig();
      if (checks >= maxChecks) {
        clearInterval(pollInterval);
        isListeningRunning.value = false;
      }
    }, 10000);
    // Desativa spinner após primeira resposta do backend
    setTimeout(() => {
      isListeningRunning.value = false;
    }, 3000);
  } catch {
    isListeningRunning.value = false;
  }
}

watch(
  () => props.selectedInbox,
  () => {
    if (props.selectedInbox) {
      fetchListeningConfig();
    }
  }
);

onMounted(() => {
  if (props.selectedInbox) {
    fetchListeningConfig();
  }
});
</script>

<template>
  <div>
    <div
      v-if="!selectedInbox"
      class="flex items-center justify-center py-12 text-n-slate-11"
    >
      {{ $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_SELECT_INBOX') }}
    </div>

    <template v-else>
      <div
        v-if="isListeningLoading"
        class="flex items-center justify-center py-12"
      >
        <Spinner />
      </div>

      <template v-else>
        <div class="flex items-center justify-between bg-n-solid-2 rounded-lg p-3 border border-n-weak mb-6">
          <div class="flex items-center gap-3">
            <span class="text-sm font-medium text-n-slate-12">Pesquisa por IA</span>
            <span class="text-xs text-n-slate-11">
              {{ aiEnabled ? 'Ativada — busca web por menções e notícias' : 'Pausada — nenhuma busca por IA será executada' }}
            </span>
          </div>
          <button
            class="px-4 py-1.5 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50"
            :class="aiEnabled ? 'bg-n-teal-9 text-white border-n-teal-9 hover:bg-n-teal-10' : 'bg-n-slate-3 text-n-slate-11 border-n-weak hover:border-n-slate-7'"
            :disabled="isTogglingAi"
            @click="toggleAi"
          >
            <Spinner v-if="isTogglingAi" size="small" />
            <span v-else>{{ aiEnabled ? 'Pausar IA' : 'Ativar IA' }}</span>
          </button>
        </div>

        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.LISTENING_CONFIG_TITLE') }}
          </h3>
          <div class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.LISTENING_CANDIDATE_NAME') }}
                </label>
                <input
                  v-model="listeningForm.candidateName"
                  type="text"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.LISTENING_CANDIDATE_USERNAME') }}
                </label>
                <input
                  v-model="listeningForm.candidateUsername"
                  type="text"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  placeholder="@username"
                />
              </div>
            </div>
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.LISTENING_HASHTAGS') }}
              </label>
              <input
                v-model="listeningForm.hashtags"
                type="text"
                class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.LISTENING_COMMA_SEPARATED')"
              />
            </div>
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.LISTENING_WATCH_TERMS') }}
              </label>
              <input
                v-model="listeningForm.watchTerms"
                type="text"
                class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.LISTENING_COMMA_SEPARATED')"
              />
            </div>
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.LISTENING_ADVERSARY_NAMES') }}
              </label>
              <input
                v-model="listeningForm.adversaryNames"
                type="text"
                class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.LISTENING_COMMA_SEPARATED')"
              />
            </div>
            <div
              v-if="listeningConfig"
              class="flex items-center gap-4 text-xs text-n-slate-11"
            >
              <span v-if="listeningConfig.last_checked_at">
                {{ $t('INSTAGRAM_MEDIA_REPORTS.LISTENING_LAST_CHECKED') }}:
                {{ formatDate(listeningConfig.last_checked_at) }}
              </span>
              <span
                v-if="listeningConfig.last_error"
                class="text-n-ruby-11"
              >
                {{ listeningConfig.last_error }}
              </span>
            </div>
            <div class="flex gap-3">
              <button
                class="px-4 py-1.5 text-sm font-medium rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-colors disabled:opacity-50"
                :disabled="isListeningSaving"
                @click="saveListeningConfig"
              >
                <Spinner v-if="isListeningSaving" size="small" />
                <span v-else>{{ $t('INSTAGRAM_MEDIA_REPORTS.LISTENING_SAVE') }}</span>
              </button>
              <button
                class="px-4 py-1.5 text-sm font-medium rounded-lg bg-n-teal-9 text-white hover:bg-n-teal-10 transition-colors disabled:opacity-50"
                :disabled="isListeningRunning"
                @click="runListening"
              >
                <Spinner v-if="isListeningRunning" size="small" />
                <span v-else>{{ $t('INSTAGRAM_MEDIA_REPORTS.LISTENING_RUN_NOW') }}</span>
              </button>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>
